import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Eye, Trash, X, UsersThree, LinkSimple } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import ResizableDialogContent from "@/components/ResizableDialog";
import SimulationReport from "@/components/SimulationReport";
import ShareLinksDialog from "@/components/ShareLinksDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIM_ROLES = ["Responsabile", "Collega", "Subordinato", "Nuovo ingresso"];

// BUG-68: Real progress bar driven by backend `/api/simulations/{id}/progress`.
// Replaces fake client-side timer. Persists across tab navigation because state
// is read from DB on every poll.
function SimProgressBar({ simId }) {
  const [progress, setProgress] = useState(null);
  useEffect(() => {
    if (!simId) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/simulations/${simId}/progress`, { withCredentials: true });
        if (!cancelled) setProgress(r.data);
        if (r.data?.status === "Completato" || r.data?.status === "Errore") return;
      } catch {/* ignore */}
      if (!cancelled) setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [simId]);
  if (!progress) {
    return (
      <div className="mt-3" data-testid="sim-progress-bar">
        <div className="h-1.5 bg-[#0D1F3C]/5 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#1B4FD8] to-[#56A3F5] animate-pulse" style={{ width: "8%" }} />
        </div>
        <p className="mt-1.5 text-[11px] text-[#0D1F3C]/50 italic">Inizializzazione…</p>
      </div>
    );
  }
  const pct = progress.step_total ? Math.min(98, Math.round((progress.step_index / progress.step_total) * 100)) : 5;
  const elapsed = progress.elapsed_seconds || 0;
  const fmt = (s) => s < 60 ? `${s}s` : `${Math.floor(s/60)}m ${s%60}s`;
  const eta = progress.eta_seconds || 0;
  return (
    <div className="mt-3" data-testid="sim-progress-bar">
      <div className="h-1.5 bg-[#0D1F3C]/5 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#1B4FD8] to-[#56A3F5] transition-all duration-700 ease-out"
          style={{ width: `${pct}%` }}
          data-testid={`sim-progress-pct-${progress.id}`}
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[11px] text-[#0D1F3C]/60">
        <span data-testid={`sim-progress-step-${progress.id}`} className="italic">{progress.step_label}</span>
        <span data-testid={`sim-progress-counter-${progress.id}`} className="font-mono">
          Elaborazione… {fmt(elapsed)}{eta > 0 ? ` · ETA ~${fmt(eta)}` : ""}
        </span>
      </div>
    </div>
  );
}

export default function SimulationsPage() {
  const [sims, setSims] = useState([]);
  const [positions, setPositions] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewSim, setViewSim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState("con_candidati");
  const [pollingSimId, setPollingSimId] = useState(null);
  const [shareLinksResource, setShareLinksResource] = useState(null);

  const [form, setForm] = useState({
    name: "",
    sim_type: "Con candidati",
    position_id: "",
    candidate_id: "",
    selected_employees: [], // { entity_id, sim_role }
  });

  const reload = () => {
    axios.get(`${API}/simulations`, { withCredentials: true }).then((r) => setSims(r.data)).catch(() => {});
  };

  const { activeWorkspaceId } = useAuth();
  // Batch 62.A: re-load on workspace switch for agency accounts
  useEffect(() => {
    reload();
    axios.get(`${API}/positions`, { withCredentials: true }).then((r) => setPositions(r.data)).catch(() => {});
    axios.get(`${API}/candidates`, { withCredentials: true }).then((r) => setCandidates(r.data)).catch(() => {});
    axios.get(`${API}/employees`, { withCredentials: true }).then((r) => setEmployees(r.data)).catch(() => {});
  }, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-poll any simulations currently "In elaborazione" (e.g. after a page reload mid-generation)
  useEffect(() => {
    const inProgress = sims.filter(s => s.status === "In elaborazione").map(s => s.id);
    if (inProgress.length === 0) return;
    if (pollingSimId) return; // Already polling one — a single polling loop is enough (reload() refreshes all)
    pollSimulation(inProgress[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sims.length]);

  const completedCandidates = useMemo(() => candidates.filter((c) => c.scores), [candidates]);
  const completedEmployees = useMemo(() => employees.filter((e) => e.scores), [employees]);

  const resetForm = () => setForm({
    name: "", sim_type: form.sim_type, position_id: "", candidate_id: "", selected_employees: [],
  });

  const toggleEmployee = (eid) => {
    setForm((p) => {
      const exists = p.selected_employees.find((x) => x.entity_id === eid);
      if (exists) {
        return { ...p, selected_employees: p.selected_employees.filter((x) => x.entity_id !== eid) };
      }
      const emp = employees.find((e) => e.id === eid);
      const defaultRole = emp?.is_manager ? "Responsabile" : "Collega";
      return { ...p, selected_employees: [...p.selected_employees, { entity_id: eid, sim_role: defaultRole }] };
    });
  };

  const updateEmployeeRole = (eid, role) => {
    setForm((p) => ({
      ...p,
      selected_employees: p.selected_employees.map((x) =>
        x.entity_id === eid ? { ...x, sim_role: role } : x
      ),
    }));
  };

  const totalParticipants = form.sim_type === "Con candidati"
    ? (form.candidate_id ? 1 : 0) + form.selected_employees.length
    : form.selected_employees.length;

  const canSubmit = totalParticipants >= 2 && totalParticipants <= 10 &&
    (form.sim_type === "Interne" || !!form.candidate_id);

  const create = async () => {
    if (!canSubmit) {
      toast.error("Servono 2-10 partecipanti totali");
      return;
    }
    const participants = [];
    if (form.sim_type === "Con candidati" && form.candidate_id) {
      participants.push({ entity_type: "candidate", entity_id: form.candidate_id, sim_role: "Nuovo ingresso" });
    }
    form.selected_employees.forEach((e) => {
      participants.push({ entity_type: "employee", entity_id: e.entity_id, sim_role: e.sim_role });
    });
    setBusy(true);
    try {
      const body = {
        name: form.name,
        sim_type: form.sim_type,
        position_id: form.position_id,
        participants,
      };
      const r = await axios.post(`${API}/simulations`, body, { withCredentials: true });
      const sim = r.data;
      toast.success("Simulazione creata, generazione report in corso...");
      setOpen(false);
      resetForm();
      reload();
      // Poll for AI completion (non-blocking, 3s interval, 120s timeout)
      if (sim?.id && sim.status !== "Completato") {
        pollSimulation(sim.id);
      } else if (sim?.status === "Completato") {
        setViewSim(sim);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const pollSimulation = (sid) => {
    setPollingSimId(sid);
    const startedAt = Date.now();
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/simulations/${sid}`, { withCredentials: true });
        const s = r.data;
        // Refresh list to keep the in-place status badge up to date
        reload();
        if (s.status === "Completato") {
          setPollingSimId(null);
          // C10: sticky toast cliccabile per navigare al risultato
          toast.success(`Simulazione "${s.name || ""}" pronta`, {
            duration: Infinity,
            id: `sim-ready-${sid}`,
            description: "Clicca per visualizzare il report",
            action: {
              label: "Apri",
              onClick: () => {
                openSim(s);
                toast.dismiss(`sim-ready-${sid}`);
              },
            },
            closeButton: true,
          });
          return;
        }
        if (s.status === "Errore") {
          setPollingSimId(null);
          toast.error(s.error_message || "Errore generazione simulazione");
          return;
        }
        if (Date.now() - startedAt > 120000) {
          setPollingSimId(null);
          toast.error("Sta impiegando piu' del previsto, riprova o contatta supporto");
          return;
        }
        setTimeout(tick, 3000);
      } catch {
        setPollingSimId(null);
      }
    };
    setTimeout(tick, 3000);
  };

  const removeSim = async (sid) => {
    if (!window.confirm("Eliminare questa simulazione?")) return;
    try {
      await axios.delete(`${API}/simulations/${sid}`, { withCredentials: true });
      toast.success("Simulazione eliminata");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const openSim = async (s) => {
    try {
      const r = await axios.get(`${API}/simulations/${s.id}`, { withCredentials: true });
      setViewSim(r.data);
    } catch (e) {
      toast.error("Errore caricamento simulazione");
    }
  };

  const renderList = (items) =>
    items.length === 0 ? (
      <Card className="border-[#0D1F3C]/8">
        <CardContent className="py-12 text-center text-[#0D1F3C]/40 text-sm">
          Nessuna simulazione in questa sezione
        </CardContent>
      </Card>
    ) : (
      <div className="space-y-3">
        {items.map((s) => {
          const isGenerating = s.status === "In elaborazione" || pollingSimId === s.id;
          return (
          <Card key={s.id} className="border-[#0D1F3C]/8 hover:border-[#1B4FD8]/30 transition-all" data-testid={`sim-row-${s.id}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-medium text-[#0D1F3C] truncate">{s.name}</h3>
                  <p className="text-sm text-[#0D1F3C]/50">
                    {new Date(s.created_at).toLocaleDateString("it-IT")}
                    {s.position_title ? ` · ${s.position_title}` : ""}
                    {s.participants_snapshot?.length ? ` · ${s.participants_snapshot.length} partecipanti` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge className={s.status === "Completato" ? "bg-green-50 text-green-700" : s.status === "Errore" ? "bg-red-50 text-red-700" : "bg-yellow-50 text-yellow-700"} data-testid={`sim-status-${s.id}`}>
                    {isGenerating ? (<><span className="inline-block w-2 h-2 rounded-full bg-yellow-500 mr-1.5 animate-pulse" />Generazione in corso…</>) : s.status}
                  </Badge>
                  {s.status === "Completato" && (
                    <Button variant="outline" size="sm" onClick={() => openSim(s)} data-testid={`sim-view-${s.id}`}>
                      <Eye size={16} className="mr-1" />Vedi Report
                    </Button>
                  )}
                  {s.status === "Completato" && (
                    <Button variant="outline" size="sm"
                      onClick={() => setShareLinksResource({ id: s.id, type: "simulation", name: s.name })}
                      title="Gestisci link condivisi (crea, copia, revoca)"
                      data-testid={`sim-share-links-${s.id}`}
                      className="border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/5">
                      <LinkSimple size={16} className="mr-1" /> Link
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => removeSim(s.id)} data-testid={`sim-delete-${s.id}`}>
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
              {isGenerating && (
                <SimProgressBar simId={s.id} />
              )}
              {s.status === "Errore" && s.error_message && (
                <p className="mt-3 text-xs text-red-600" data-testid={`sim-error-${s.id}`}>{s.error_message}</p>
              )}
            </CardContent>
          </Card>
        );})}
      </div>
    );

  const withCand = sims.filter((s) => s.sim_type === "Con candidati");
  const internal = sims.filter((s) => s.sim_type === "Interne");

  return (
    <div className="animate-fade-in" data-testid="simulations-page">
      <ShareLinksDialog
        open={!!shareLinksResource}
        onOpenChange={(o) => { if (!o) setShareLinksResource(null); }}
        resource={shareLinksResource}
      />
      <PageHeader title="Simulazione Assunzione">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-simulation-btn" className="bg-[#1B4FD8] hover:bg-[#0D1F3C]">
              <Plus size={18} className="mr-2" />Nuova Simulazione
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-simulation-dialog">
            <DialogHeader>
              <DialogTitle>Nuova Simulazione Team</DialogTitle>
              <DialogDescription>Componi il team (2-10 partecipanti) e avvia l'analisi di compatibilita.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Toggle type */}
              <div className="grid grid-cols-2 gap-2 p-1 bg-[#F5F6FA] rounded-lg">
                <button
                  data-testid="sim-type-candidati"
                  onClick={() => setForm((p) => ({ ...p, sim_type: "Con candidati" }))}
                  className={`py-2 px-3 rounded-md text-sm font-medium transition-all ${form.sim_type === "Con candidati" ? "bg-white text-[#1B4FD8] shadow-sm" : "text-[#0D1F3C]/60"}`}
                >
                  Con candidati
                </button>
                <button
                  data-testid="sim-type-interne"
                  onClick={() => setForm((p) => ({ ...p, sim_type: "Interne", candidate_id: "" }))}
                  className={`py-2 px-3 rounded-md text-sm font-medium transition-all ${form.sim_type === "Interne" ? "bg-white text-[#1B4FD8] shadow-sm" : "text-[#0D1F3C]/60"}`}
                >
                  Interne
                </button>
              </div>

              <div>
                <Label className="text-xs mb-1 block">Nome simulazione</Label>
                <Input
                  data-testid="sim-name"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="es. Nuovo Responsabile Commerciale"
                />
              </div>

              <div>
                <Label className="text-xs mb-1 block">Posizione di riferimento (opzionale)</Label>
                <Select value={form.position_id} onValueChange={(v) => setForm((p) => ({ ...p, position_id: v }))}>
                  <SelectTrigger data-testid="sim-position"><SelectValue placeholder="Seleziona posizione" /></SelectTrigger>
                  <SelectContent>
                    {positions.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {form.sim_type === "Con candidati" && (
                <div>
                  <Label className="text-xs mb-1 block">Candidato (analisi completata) *</Label>
                  <Select value={form.candidate_id} onValueChange={(v) => setForm((p) => ({ ...p, candidate_id: v }))}>
                    <SelectTrigger data-testid="sim-candidate"><SelectValue placeholder="Seleziona candidato" /></SelectTrigger>
                    <SelectContent>
                      {completedCandidates.length === 0
                        ? <div className="px-3 py-2 text-xs text-[#0D1F3C]/50">Nessun candidato con analisi completata</div>
                        : completedCandidates.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>
                          ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Employees multi-select */}
              <div>
                <Label className="text-xs mb-2 block">
                  Dipendenti coinvolti {form.sim_type === "Con candidati" ? "(1-9)" : "(2-10)"} — selezionati: {form.selected_employees.length}
                </Label>
                <div className="border border-[#0D1F3C]/10 rounded-lg max-h-[260px] overflow-y-auto divide-y divide-[#0D1F3C]/5">
                  {completedEmployees.length === 0 ? (
                    <div className="p-4 text-xs text-[#0D1F3C]/50 text-center">Nessun dipendente con analisi completata</div>
                  ) : (
                    completedEmployees.map((e) => {
                      const selected = form.selected_employees.find((x) => x.entity_id === e.id);
                      return (
                        <div key={e.id} className="flex items-center gap-3 p-3 hover:bg-[#F5F6FA]/50" data-testid={`sim-emp-row-${e.id}`}>
                          <Checkbox
                            checked={!!selected}
                            onCheckedChange={() => toggleEmployee(e.id)}
                            data-testid={`sim-emp-check-${e.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-[#0D1F3C] truncate">
                              {e.first_name} {e.last_name}
                            </div>
                            <div className="text-xs text-[#0D1F3C]/50 truncate">
                              {e.job_title || "—"} {e.is_manager && "· Responsabile"}
                            </div>
                          </div>
                          {selected && (
                            <Select
                              value={selected.sim_role}
                              onValueChange={(v) => updateEmployeeRole(e.id, v)}
                            >
                              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid={`sim-emp-role-${e.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {SIM_ROLES.map((r) => (
                                  <SelectItem key={r} value={r}>{r}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between text-xs pt-2 border-t border-[#0D1F3C]/8">
                <span className="text-[#0D1F3C]/60">
                  Partecipanti totali: <strong className={totalParticipants >= 2 && totalParticipants <= 10 ? "text-[#1B4FD8]" : "text-red-500"}>{totalParticipants}</strong> / 10
                </span>
                <span className="text-[#0D1F3C]/40">Costo: 1 analisi</span>
              </div>

              <Button
                onClick={create}
                disabled={!canSubmit || busy}
                data-testid="sim-create-btn"
                className="w-full bg-[#1B4FD8] hover:bg-[#0D1F3C] disabled:opacity-50"
              >
                {busy ? "Generazione in corso..." : "Crea Simulazione"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {/* Report viewer */}
      <Dialog open={!!viewSim} onOpenChange={() => setViewSim(null)}>
        <ResizableDialogContent defaultW={1100} defaultH={780}>
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>{viewSim?.name}</DialogTitle>
            <DialogDescription className="sr-only">Report dettagliato della simulazione team</DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {viewSim && <SimulationReport sim={viewSim} />}
          </div>
        </ResizableDialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-[#0D1F3C]/8 p-1 rounded-lg mb-6">
          <TabsTrigger value="con_candidati" data-testid="tab-con-candidati" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-6">
            Con Candidati ({withCand.length})
          </TabsTrigger>
          <TabsTrigger value="interne" data-testid="tab-interne" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-6">
            Interne ({internal.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="con_candidati">{renderList(withCand)}</TabsContent>
        <TabsContent value="interne">{renderList(internal)}</TabsContent>
      </Tabs>
    </div>
  );
}
